import React, { useState } from "react";
import { Wallet, DollarSign, Users, CheckCircle, BarChart2, Building, Eye, Check, X, Download, History, AlertTriangle, Upload, Trash2, FileText, Gift, Search, ChevronRight, ChevronLeft, Settings, Zap, Calendar, ShieldCheck } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, Av, Tbl, Sec, KpiStrip } from "../components";
import { notify, confirmDialog } from "../notify";
import { fmtDMY } from "../dateFormat";

export const PayrollPage = ({ payData, payReports, role, onRefresh, onNavigate, companies = [], can: perm = () => true }) => {
  const [payTab, setPayTab] = useState("overview");
  const [payPeriod, setPayPeriod] = useState("");
  const [paySlips, setPaySlips] = useState([]);
  const [payBankFile, setPayBankFile] = useState(null);
  // Which bank file is being viewed: 'base' (salary) or 'incentive'.
  const [bankType, setBankType] = useState('base');
  // Detail drawer — { slip, ytd } from /slips/:period/:eid/detail. Opens
  // when an admin/HR clicks "View" on a row. Shows the slip side-by-side
  // with FY-to-date totals so HR can spot YTD anomalies before locking.
  const [slipDetail, setSlipDetail] = useState(null);
  // Incentives — staged rows for the picked period + company. New 2-amount
  // format: amount_to_transfer + tds_amount per row. Incentives are a
  // SEPARATE payment (own bank file) and combine onto the payslip only.
  const EMPTY_INC = { rows: [], total_transfer: 0, total_tds: 0, total_gross: 0, count: 0 };
  const [incentives, setIncentives] = useState(EMPTY_INC);
  const [incUploadResult, setIncUploadResult] = useState(null);
  const [incBusy, setIncBusy] = useState(false);
  // Per-row busy marker (`${id}:approve|reject|delete`) for the incentives table.
  const [incRowBusy, setIncRowBusy] = useState(null);
  const [approveAllBusy, setApproveAllBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [lockingId, setLockingId] = useState(null);
  // Pre-flight preview before /process — shows who would be processed and
  // who would be silently skipped (no annual salary on file), so users
  // catch missing salary data BEFORE generating a one-employee payroll.
  const [processPreview, setProcessPreview] = useState(null); // { period, company, totals, eligible, skipped, busy }
  const openSlipDetail = (period, eid) => {
    setSlipDetail({ loading: true });
    api.get(`/api/payroll/slips/${period}/${encodeURIComponent(eid)}/detail`)
       .then(d => setSlipDetail(d.data || d))
       .catch(e => setSlipDetail({ error: e.message || 'Load failed' }));
  };

  // Load staged incentives for (period, company).
  const loadIncentives = (period, companyId) => {
    if (!period) { setIncentives(EMPTY_INC); return; }
    const cq = companyId ? `&company=${companyId}` : '';
    api.get(`/api/payroll/incentives/${period}?_=${Date.now()}${cq}`)
       .then(d => setIncentives(d.data || d))
       .catch(() => setIncentives(EMPTY_INC));
  };

  // File upload — multipart POST. Response = { staged, skipped } so HR
  // sees which rows landed and which were skipped (and why).
  const uploadIncentives = async (file) => {
    if (!payPeriod) return notify('Pick a period first');
    if (!selCompany) return notify('Pick a company first');
    setIncBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const url = `${BASE}/api/payroll/incentives/${payPeriod}/upload?company=${selCompany}`;
      const r = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('hrms_token')}` },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Upload failed');
      setIncUploadResult(j);
      loadIncentives(payPeriod, selCompany);
      refreshPendingInc();
      if (payBankFile && bankType === 'incentive') loadBankFile(payPeriod, 'incentive');
      notify(`${j.uploaded_count || 0} incentive row(s) staged${j.skipped_count ? `, ${j.skipped_count} skipped` : ''}`, { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Upload failed', { title: 'Error', variant: 'error' }); }
    finally { setIncBusy(false); }
  };
  const deleteIncentive = async (id) => {
    if (!(await confirmDialog('Delete this staged incentive row?'))) return;
    setIncRowBusy(`${id}:delete`);
    try {
      await api.del(`/api/payroll/incentives/${id}`);
      loadIncentives(payPeriod, selCompany);
      if (payBankFile && bankType === 'incentive') loadBankFile(payPeriod, 'incentive');
      notify('Incentive row deleted', { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Delete failed', { title: 'Error', variant: 'error' }); }
    finally { setIncRowBusy(null); }
  };
  // Admin-only incentive approval. Non-admin uploads are 'pending' until
  // an admin approves them; only approved incentives reach the bank file
  // / payslip.
  const approveIncentive = async (id) => {
    setIncRowBusy(`${id}:approve`);
    try { await api.put(`/api/payroll/incentives/${id}/approve`, {}); loadIncentives(payPeriod, selCompany); refreshPendingInc(); if (payBankFile && bankType === 'incentive') loadBankFile(payPeriod, 'incentive'); notify('Incentive approved', { title: 'Approved', variant: 'success' }); }
    catch (e) { notify(e.message || 'Approve failed', { title: 'Error', variant: 'error' }); }
    finally { setIncRowBusy(null); }
  };
  const rejectIncentive = async (id) => {
    if (!(await confirmDialog('Reject this incentive? It will not be paid.'))) return;
    setIncRowBusy(`${id}:reject`);
    try { await api.put(`/api/payroll/incentives/${id}/reject`, {}); loadIncentives(payPeriod, selCompany); refreshPendingInc(); notify('Incentive rejected', { title: 'Rejected', variant: 'success' }); }
    catch (e) { notify(e.message || 'Reject failed', { title: 'Error', variant: 'error' }); }
    finally { setIncRowBusy(null); }
  };
  const approveAllIncentives = async () => {
    if (!(await confirmDialog(`Approve all pending incentives for ${fmtP(payPeriod)}?`))) return;
    setApproveAllBusy(true);
    try { const r = await api.post(`/api/payroll/incentives/${payPeriod}/approve-all?company=${selCompany}`, {}); notify(`Approved ${r.approved} incentive(s)`, { title: 'Approved', variant: 'success' }); loadIncentives(payPeriod, selCompany); refreshPendingInc(); if (payBankFile && bankType === 'incentive') loadBankFile(payPeriod, 'incentive'); }
    catch (e) { notify(e.message || 'Approve failed', { title: 'Error', variant: 'error' }); }
    finally { setApproveAllBusy(false); }
  };

  // Generate Payslips — explicit publish step (combined base + incentives).
  const generatePayslips = async () => {
    if (!selCompany) return notify('Pick a company first');
    const run = uniqueRunsForCompany.find(r => r.period === payPeriod);
    if (!run) return notify('Process payroll for this period first.');
    if (!(await confirmDialog(`Generate salary slips for ${fmtP(payPeriod)} (base salary + incentives)?`))) return;
    setGenerating(true);
    try {
      const r = await api.post(`/api/payroll/generate-payslips/${payPeriod}`, { company_id: parseInt(selCompany) });
      notify(r.message || 'Payslips generated', { title: 'Saved', variant: 'success' });
      loadSlips(payPeriod);
      onRefresh();
    } catch (e) { notify(e.message || 'Generate failed', { title: 'Error', variant: 'error' }); }
    finally { setGenerating(false); }
  };
  // Selected company for the page. Defaults to the first company; admin
  // can switch to "All companies" (where supported) or any specific one.
  // Backend /process now requires company_id, and reads honour the filter.
  const [selCompany, setSelCompany] = useState("");
  const [topSearch, setTopSearch] = useState("");
  const [topDept, setTopDept] = useState("");
  const [topPage, setTopPage] = useState(1);
  const [topPageSize, setTopPageSize] = useState(5);
  React.useEffect(() => { setTopPage(1); }, [topSearch, topDept, selCompany]);

  const PD = payData || {}; const isEmpView = PD.view === "employee";
  const fmt$ = (v) => v >= 10000000 ? "₹" + (v / 10000000).toFixed(1) + "Cr" : v >= 100000 ? "₹" + (v / 100000).toFixed(1) + "L" : "₹" + (v || 0).toLocaleString('en-IN');
  const mths = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fmtP = (p) => { if (!p) return "—"; const [y, m] = p.split("-"); return mths[parseInt(m)] + " " + y; };
  const stClrPay = (s) => s === "Locked" ? K.g : s === "Completed" ? K.bl : s === "Processing" ? K.a : K.t3;
  // Payroll runs for the immediately previous calendar month only.
  const prevMonthPeriod = (() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();

  // Default company picker on first render (admin sees the first available).
  React.useEffect(() => {
    if (!selCompany && companies.length > 0) {
      setSelCompany(String(companies[0].id));
    }
  }, [companies, selCompany]);

  // If the current tab isn't allowed for this role (e.g. centre_head can
  // only see Incentives), snap to the first visible tab.
  React.useEffect(() => {
    if (pTabs.length && !pTabs.includes(payTab)) setPayTab(pTabs[0]);
  }, [role]); // eslint-disable-line

  // Org-wide pending-incentive count (admin/accounts) — powers the
  // "needs approval" badge so the admin doesn't hunt per-company.
  const [pendingInc, setPendingInc] = React.useState({ total: 0, groups: [] });
  const refreshPendingInc = React.useCallback(() => {
    if (!perm('payroll', 'incentives', null, 'view')) return;
    api.get(`/api/payroll/incentives/pending-count?_=${Date.now()}`)
       .then(d => setPendingInc(d.data || d))
       .catch(() => {});
  }, [perm]);
  React.useEffect(() => { refreshPendingInc(); }, [refreshPendingInc]);

  // Jump to a pending group's (company, period) on the Incentives tab.
  // Switching company resets payPeriod via the company effect, so we
  // stash the target and apply the period once the company settles.
  const [pendingJump, setPendingJump] = React.useState(null);
  const jumpToPending = (g) => {
    setPayTab('incentives');
    if (String(g.company_id) === selCompany) {
      setPayPeriod(g.period); loadIncentives(g.period, selCompany);
    } else {
      setPendingJump(g); setSelCompany(String(g.company_id));
    }
  };
  React.useEffect(() => {
    if (pendingJump && String(pendingJump.company_id) === selCompany) {
      setPayPeriod(pendingJump.period);
      loadIncentives(pendingJump.period, selCompany);
      setPendingJump(null);
    }
  }, [selCompany, pendingJump]); // eslint-disable-line

  // When the company switches, refetch dashboard + reports so the runs,
  // trend chart, components panel, and dept breakdown all reflect the
  // selected company (Reports endpoint was previously fetched once
  // globally, which produced "Apr 2026 · Apr 2026 · Apr 2026" trend bars
  // — one for each company's run).
  const [localPay, setLocalPay] = useState(null);
  const [localReports, setLocalReports] = useState(null);
  React.useEffect(() => {
    if (!selCompany) return;
    setPayPeriod('');
    setPaySlips([]);
    setPayBankFile(null);
    setIncentives(EMPTY_INC);
    setIncUploadResult(null);
    api.get(`/api/payroll?company=${selCompany}`)
       .then(d => setLocalPay(d.data || d))
       .catch(() => {});
    api.get(`/api/payroll/reports?company=${selCompany}`)
       .then(d => setLocalReports(d.data || d))
       .catch(() => {});
  }, [selCompany]);
  // Use refreshed company-scoped data when present, fall back to parent prop
  const effPay = localPay || PD;
  const effReports = localReports || payReports || {};
  const cQuery = selCompany ? `&company=${selCompany}` : '';

  const runPayroll = async (period) => {
    if (!selCompany) return notify('Pick a company first — payroll runs are per-company');
    // Step 1: preview — show which employees would be processed vs silently skipped.
    setPreviewBusy(true);
    try {
      const p = await api.post('/api/payroll/process/preview', { period, company_id: parseInt(selCompany) });
      setProcessPreview({ ...p, period });
    } catch (e) { notify(e.message || 'Could not preview payroll', { title: 'Error', variant: 'error' }); }
    finally { setPreviewBusy(false); }
  };
  // Step 2: user confirmed in the modal → actually process.
  const confirmRunPayroll = async (ids = null) => {
    if (!processPreview) return;
    const { period } = processPreview;
    setProcessPreview(p => p && { ...p, busy: true });
    try {
      const r = await api.post('/api/payroll/process', { period, company_id: parseInt(selCompany), ...(ids && ids.length ? { employee_ids: ids } : {}) });
      setProcessPreview(null);
      notify((r.partial ? 'Payroll processed for the selected employees: ' : 'Payroll processed: ') + r.empCount + ' employee' + (r.empCount === 1 ? '' : 's') + ', Net ' + fmt$(r.net) + (r.notProcessed?.length ? ` · ${r.notProcessed.length} skipped (no salary on file)` : ''), { title: 'Saved', variant: 'success' });
      onRefresh();
    } catch (e) {
      setProcessPreview(p => p && { ...p, busy: false });
      notify(e.message || 'Error processing payroll', { title: 'Error', variant: 'error' });
    }
  };
  const lockPayroll = async (runId) => {
    setLockingId(runId);
    try { await api.put('/api/payroll/lock/' + runId); onRefresh(); notify('Payroll locked', { title: 'Saved', variant: 'success' }); }
    catch (e) { notify(e.message || 'Error locking payroll', { title: 'Error', variant: 'error' }); }
    finally { setLockingId(null); }
  };
  const loadSlips = (period) => { setPayPeriod(period); api.get(`/api/payroll/slips?period=${period}${cQuery}`).then(d => setPaySlips(d.data || d || [])); };
  // Bank file: type 'base' (salary) or 'incentive'. Both read live from
  // the DB (base = slips net pay, incentive = SUM amount_to_transfer).
  const loadBankFile = (period, type = bankType) => {
    setBankType(type);
    api.get(`/api/payroll/bank-file/${period}?type=${type}&${cQuery.replace(/^&/, '')}`).then(d => setPayBankFile(d.data || d));
  };

  // CSV writer for the bank-specific file formats. Each bank's NEFT batch
  // layout differs (column order, header row, amount precision). Wrapped
  // here so the UI just picks a name and we know what to emit.
  const downloadBankFile = (format, bf) => {
    const fname = `bank-${format}-${bf.period}.csv`;
    let header, rows;
    if (format === 'hdfc') {
      // HDFC bulk upload: header optional; cols = Beneficiary Name, Account No, IFSC, Amount, Mode, Remarks
      header = 'Beneficiary Name,Account Number,IFSC,Amount,Mode,Remarks';
      rows = bf.transfers.map(t => [JSON.stringify(t.nm || ''), t.acct || '', t.ifsc || '', t.net, 'NEFT', `Salary ${bf.period}`].join(','));
    } else if (format === 'icici') {
      // ICICI Corporate Internet Banking: Pay Mode, Beneficiary A/c, IFSC, Amount, Name, Narration
      header = 'Pay Mode,Beneficiary A/c No,IFSC,Amount,Beneficiary Name,Narration';
      rows = bf.transfers.map(t => ['NEFT', t.acct || '', t.ifsc || '', t.net, JSON.stringify(t.nm || ''), `Salary ${bf.period}`].join(','));
    } else if (format === 'axis') {
      // Axis CMS upload: Acct No, IFSC, Amount, Beneficiary, Email, Narration
      header = 'Account No,IFSC Code,Amount,Beneficiary Name,Email,Narration';
      rows = bf.transfers.map(t => [t.acct || '', t.ifsc || '', t.net, JSON.stringify(t.nm || ''), '', `Salary ${bf.period}`].join(','));
    } else {
      // generic
      header = 'Employee ID,Name,Net Pay,Bank,Account No,IFSC,Mode';
      rows = bf.transfers.map(t => [t.eid, JSON.stringify(t.nm || ''), t.net, JSON.stringify(t.bank || ''), t.acct || '', t.ifsc || '', t.mode || 'NEFT'].join(','));
    }
    const csv = header + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
  };

  // Server-side CSV downloads for the new payroll register / statutory /
  // over-paid endpoints. Auth header required so we fetch + blob then save.
  const serverCsv = async (path, suggestedName) => {
    try {
      const r = await fetch(`${BASE}${path}`, { headers: { Authorization: 'Bearer ' + api.getToken() } });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = suggestedName; a.click();
    } catch (e) { notify(e.message || 'Download failed'); }
  };

  // ── Role-based capabilities (mirror the backend guards) ──
  //   process            : admin only
  //   upload incentive    : admin, centre_head, accounts
  //   view incentive list : admin, accounts
  //   approve / delete    : admin only
  //   generate / bank / lock : admin, accounts
  //
  // Computed above the isEmpView early return below — the pTabs-snapping
  // useEffect earlier in this component references pTabs unconditionally
  // on every render (including the employee view), so pTabs must always be
  // assigned before that effect fires, not skipped by the early return.
  const isAdmin = role === 'admin';
  const can = {
    process:        perm('payroll', 'process', null, 'edit'),
    processSelected: perm('payroll', 'process', 'process_selected', 'edit'),
    uploadIncentive: perm('payroll', 'incentives', 'upload_incentive', 'edit'),
    viewIncentive:  perm('payroll', 'incentives', null, 'view'),
    approveIncentive: perm('payroll', 'incentives', 'approve_incentive', 'edit'),
    deleteIncentive: perm('payroll', 'incentives', 'delete_incentive', 'edit'),
    generate:       perm('payroll', 'payslips', 'generate_payslips', 'edit'),
    bank:           perm('payroll', 'bank', null, 'view'),
    lock:           perm('payroll', 'bank', 'lock_payroll', 'edit'),
    reports:        perm('payroll', 'reports', null, 'view'),
  };
  // Tab visibility per role. Centre Head sees ONLY the incentive upload.
  const allTabs = ["overview", "process", "incentives", "payslips", "reports", "bank"];
  const visibleTab = (t) => {
    if (t === 'process')    return can.process;
    if (t === 'incentives') return can.uploadIncentive || can.viewIncentive;
    if (t === 'bank')       return can.bank;
    if (t === 'reports')    return can.reports;
    if (t === 'overview')   return perm('payroll', 'overview', null, 'view');
    if (t === 'payslips')   return perm('payroll', 'payslips', null, 'view');
    return true;
  };
  const pTabs = allTabs.filter(visibleTab);

  if (isEmpView) {
    const slips = PD.slips || [];
    return <div>
      <h1 className="mt-0 mb-4 text-2xl font-extrabold text-t0">My Payslips</h1>
      {slips.length > 0 ? <Tbl cols={[
        { k: "period", l: "Month", r: r => <span className="font-semibold">{fmtP(r.period)}</span> },
        { k: "gross", l: "Gross", r: r => <span className="text-[14px]">{fmt$(parseFloat(r.gross))}</span> },
        { k: "ded", l: "Deductions", r: r => <span className="text-[14px] text-r">{fmt$(parseFloat(r.total_deductions))}</span> },
        { k: "net", l: "Net Pay", r: r => <span className="font-bold text-g">{fmt$(parseFloat(r.net_pay))}</span> },
        { k: "st", l: "Status", r: r => <Bg c={r.status === "Paid" ? K.g : K.a} sm dot>{r.status}</Bg> },
      ]} data={slips} /> : <div className="text-center p-[30px] text-t3">No payslips available</div>}
    </div>;
  }

  // Source of truth: effPay (company-scoped refetch) when available, else PD.
  const stats = effPay.stats || {};
  const runs = effPay.runs || [];
  const topE = effPay.topEmployees || [];
  const curPeriod = effPay.currentPeriod || PD.currentPeriod || "";
  const curRun = effPay.currentRun || PD.currentRun;
  const PR = effReports || {}; const trend = PR.trend || []; const components = PR.components || []; const deptWise = PR.deptWise || [];

  const tabLabel = (t) => t === "overview" ? "Overview" : t === "process" ? "Process Payroll" : t === "incentives" ? "Incentives" : t === "payslips" ? "Payslips" : t === "reports" ? "Reports" : "Bank File";
  const PTabBar = () => (
    <div className="flex gap-0 mb-3 border-b border-b0 flex-wrap whitespace-nowrap">
      {pTabs.map(t => (
        <button
          key={t}
          onClick={() => setPayTab(t)}
          className={`px-4 py-[7px] bg-transparent border-0 text-[14px] cursor-pointer inline-flex items-center gap-[5px] ${payTab === t ? "border-b-2 border-p text-p font-semibold" : "border-b-2 border-transparent text-t2 font-medium"}`}
        >
          {tabLabel(t)}
          {/* Pending-approval badge on the Incentives tab (admin/accounts). */}
          {t === "incentives" && pendingInc.total > 0 &&
            <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-[4px] rounded-full text-[12px] font-bold text-white" style={{ background: K.a }}>{pendingInc.total}</span>}
        </button>
      ))}
    </div>
  );

  // Period options derived from runs — filtered to the selected company so
  // the dropdown shows each period only once (no duplicate Apr 2026 across
  // company runs). Defensive deduplication: even if `runs` arrives with
  // rows from multiple companies during a transient render (PD loaded but
  // localPay refetch in flight), we keep only the first occurrence of
  // each period so the dropdown is never duplicated.
  const runsForCompany = selCompany
    ? runs.filter(r => String(r.company_id) === selCompany)
    : runs;
  const seenPeriods = new Set();
  const uniqueRunsForCompany = runsForCompany.filter(r => {
    if (seenPeriods.has(r.period)) return false;
    seenPeriods.add(r.period);
    return true;
  });
  const periodOptions = [
    { v: '', l: 'All periods' },
    ...uniqueRunsForCompany.map(r => ({ v: r.period, l: fmtP(r.period) + (r.status ? ` · ${r.status}` : '') })),
  ];

  // ── Header/overview derived values — all computed from real data already
  // on the page (no fabricated statutory rates, cutoff dates, or per-row
  // "Ready to Disburse" statuses that we don't actually track). ──
  const fyStartYear = (() => { const n = new Date(); return n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1; })();
  const fyLabel = `FY ${fyStartYear}–${String(fyStartYear + 1).slice(2)}`;
  const cyclePeriod = payPeriod || curPeriod;
  const cycleRange = (() => {
    if (!cyclePeriod) return null;
    const [y, m] = cyclePeriod.split('-').map(Number);
    const first = new Date(y, m - 1, 1); const last = new Date(y, m, 0);
    return `${fmtDMY(first)} – ${fmtDMY(last)}`;
  })();
  const lockedRunsCount = uniqueRunsForCompany.filter(r => r.status === "Locked").length;
  const currentRunForPeriod = uniqueRunsForCompany.find(r => r.period === payPeriod);

  // Top Earners table — search/dept filter + pagination + CSV export, all
  // over the real topEmployees list the dashboard endpoint already returns.
  const topDepts = Array.from(new Set(topE.map(e => e.dept).filter(Boolean)));
  const topSearchQ = topSearch.trim().toLowerCase();
  const filteredTopE = topE.filter(e =>
    (!topDept || e.dept === topDept) &&
    (!topSearchQ || (e.nm || '').toLowerCase().includes(topSearchQ) || (e.rl || '').toLowerCase().includes(topSearchQ))
  );
  const topTotalPages = Math.max(1, Math.ceil(filteredTopE.length / topPageSize));
  const topSafePage = Math.min(topPage, topTotalPages);
  const topPageStart = (topSafePage - 1) * topPageSize;
  const pagedTopE = filteredTopE.slice(topPageStart, topPageStart + topPageSize);
  const topPageNumbers = (() => {
    if (topTotalPages <= 7) return Array.from({ length: topTotalPages }, (_, i) => i + 1);
    const set = new Set([1, topTotalPages, topSafePage, topSafePage - 1, topSafePage + 1]);
    const arr = Array.from(set).filter(n => n >= 1 && n <= topTotalPages).sort((a, b) => a - b);
    const out = []; arr.forEach((n, i) => { if (i > 0 && n - arr[i - 1] > 1) out.push('…'); out.push(n); }); return out;
  })();
  // A real per-row status: whether a payslip already exists for the
  // selected period (from `paySlips`, loaded whenever a period is picked),
  // not an invented "Ready to Disburse" flag we don't actually track.
  const slipForEmp = (id) => paySlips.find(s => s.eid === id);
  const exportTopEarnersCsv = () => {
    const header = ["Employee ID", "Name", "Role", "Department", "CTC/Year", "Monthly Gross"];
    const rows = filteredTopE.map(e => [e.id, e.nm, e.rl, e.dept, e.sal, Math.round(e.sal / 12)]);
    const csv = [header, ...rows].map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `top-earners-${selCompany || 'all'}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return <div>
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
      <div>
        <div className="flex items-center gap-[6px] text-[12px] font-semibold text-t3 mb-[3px]">
          <span>Finance</span><ChevronRight size={11} /><span className="text-t1">Payroll &amp; Salary Slabs</span>
        </div>
        <h1 className="m-0 text-2xl font-extrabold text-t0 tracking-tight flex items-center gap-[10px]">
          Payroll &amp; Compensation
          <span className="text-[12px] font-bold px-[10px] py-[2px] rounded-full" style={{ background: K.pg, color: K.p }}>{fyLabel}</span>
          {curRun && <Bg c={stClrPay(curRun.status)} dot sm>{curRun.status}</Bg>}
        </h1>
        <div className="text-[13px] text-t3 mt-[2px]">Manage monthly disbursements, salary structures, tax compliance (PF/ESI/TDS), and employee CTC.</div>
      </div>
      <div className="flex items-center gap-[8px] flex-wrap">
        {onNavigate && <Bt v="secondary" s="sm" icon={Settings} onClick={() => onNavigate('settings')}>Payroll Settings</Bt>}
        {can.bank && <Bt v="secondary" s="sm" icon={Download} onClick={() => setPayTab('bank')}>Bank Disbursement File</Bt>}
        {can.process && <Bt icon={Zap} s="sm" onClick={() => setPayTab('process')}>Run Payroll Wizard</Bt>}
      </div>
    </div>

    {/* Top-of-page filter bar — Company first (the harder choice), Period
        second (filtered by company). Same pattern across Process Payroll,
        Payslips, Reports, Bank File so the user never has to pick twice. */}
    <Cd style={{ marginBottom: 12 }}>
      <div className="flex gap-[14px] items-end flex-wrap justify-between">
        <div className="flex gap-[14px] items-end flex-wrap">
          <div className="min-w-[180px]">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide mb-[4px]">Company Entity</div>
            <Sl options={companies.map(c => ({ v: String(c.id), l: c.name }))}
                value={selCompany} onChange={setSelCompany} />
          </div>
          <div className="min-w-[200px]">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide mb-[4px]">Payroll Period</div>
            <Sl options={periodOptions} value={payPeriod}
                onChange={(v) => { setPayPeriod(v); if (v) { loadSlips(v); loadBankFile(v); loadIncentives(v, selCompany); } }} />
          </div>
          <div className="text-[13px] text-t3 self-center pb-[7px] flex items-center gap-[6px]">
            <span className="w-[6px] h-[6px] rounded-full bg-p flex-shrink-0" />
            {uniqueRunsForCompany.length} run{uniqueRunsForCompany.length === 1 ? '' : 's'} for this company
          </div>
        </div>
        {cycleRange && <div className="flex items-center gap-[8px] bg-bg2 border border-b0 px-[12px] py-[9px] rounded-xl text-[13px] text-t2 flex-shrink-0">
          <Calendar size={14} className="text-t3" />
          <span>Cycle: <strong className="text-t0">{cycleRange}</strong></span>
        </div>}
      </div>
    </Cd>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
      {[
        { icon: Wallet, label: "Monthly Total", value: fmt$(stats.monthlyTotal || 0), sub: "This company", c: K.p },
        { icon: DollarSign, label: "Avg CTC", value: fmt$(stats.avgSalary || 0), sub: "Annualized base", c: K.tl },
        { icon: Users, label: "Employees", value: stats.empCount || 0, sub: "Active headcount", c: K.bl },
        { icon: CheckCircle, label: "Runs Locked", value: `${lockedRunsCount}/${uniqueRunsForCompany.length}`, sub: "This company", c: K.g },
        { icon: ShieldCheck, label: "Total Deductions", value: currentRunForPeriod ? fmt$(currentRunForPeriod.deductions) : "—", sub: currentRunForPeriod ? fmtP(payPeriod) : "Pick a period", c: K.pu },
      ].map((s, i) => (
        <div key={i} className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)] hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)] transition-shadow flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide truncate">{s.label}</div>
            <div className="text-[22px] font-extrabold text-t0 tracking-tight mt-[2px]">{s.value}</div>
            <div className="text-[11px] text-t3 mt-[2px] truncate">{s.sub}</div>
          </div>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.c}14`, color: s.c }}>
            <s.icon size={20} />
          </div>
        </div>
      ))}
    </div>

    <PTabBar />

    {payTab === "overview" && <Cd style={{ padding: 0, overflow: "hidden" }}>
      <div className="p-5 border-b border-b0 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: K.pg, color: K.p }}><Users size={18} /></div>
          <div>
            <div className="text-[15px] font-bold text-t0">Top Earners &amp; Salary Distribution</div>
            <div className="text-[12px] text-t3">Comprehensive breakdown of monthly gross compensation and deductions.</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-[8px]">
          <div className="w-[200px]"><Inp icon={Search} placeholder="Filter employee or role…" value={topSearch} onChange={setTopSearch} /></div>
          {topDepts.length > 0 && <div className="w-[160px]"><Sl options={[{ v: "", l: "All Departments" }, ...topDepts.map(d => ({ v: d, l: d }))]} value={topDept} onChange={setTopDept} /></div>}
          <Bt v="secondary" s="sm" icon={Download} onClick={exportTopEarnersCsv}>Export CSV</Bt>
        </div>
      </div>
      {pagedTopE.length > 0 ? <>
        <Tbl cols={[
          { k: "nm", l: "Employee", r: r => <div className="flex items-center gap-[8px] min-w-0"><Av nm={r.nm} sz={30} /><div className="min-w-0"><div className="font-bold text-t0 text-[14px] truncate">{r.nm}</div><div className="text-[11px] text-t3 truncate">ID: {r.id}</div></div></div> },
          { k: "rl", l: "Role", r: r => <span className="text-[13px] font-semibold text-t1">{r.rl || "—"}</span> },
          { k: "dept", l: "Dept", r: r => r.dept ? <Bg sm>{r.dept}</Bg> : <span className="text-t3 text-[12px]">—</span> },
          { k: "ctc", l: "CTC / YR", r: r => <span className="font-bold text-[13px] text-t0">{fmt$(r.sal)}</span> },
          { k: "m", l: "Monthly Gross", r: r => <span className="text-[13px] font-semibold text-t1">{fmt$(r.sal / 12)}</span> },
          { k: "pf", l: "PF / Deductions", r: r => <span className="text-[13px] font-semibold text-r">{fmt$(Math.min(r.sal * 0.4 / 12, 15000) * 0.12)}</span> },
          { k: "st", l: "Status", r: r => {
              if (!payPeriod) return <span className="text-t3 text-[12px]">—</span>;
              const slip = slipForEmp(r.id);
              if (slip) return <Bg c={slip.status === "Paid" ? K.g : K.a} sm dot>{slip.status}</Bg>;
              return currentRunForPeriod ? <Bg c={K.a} sm dot>Slip Pending</Bg> : <Bg c={K.t3} sm>Not Processed</Bg>;
            } },
          { k: "act", l: "Actions", r: r => <div className="flex items-center justify-end gap-[10px]">
            <button disabled={!payPeriod} onClick={() => openSlipDetail(payPeriod, r.id)} className={`bg-transparent border-0 text-[12px] font-bold p-0 ${payPeriod ? "text-p cursor-pointer hover:underline" : "text-t3 cursor-not-allowed"}`}>Breakdown</button>
            <button disabled={!slipForEmp(r.id)} onClick={() => window.open(`${BASE}/api/payroll/slips/${payPeriod}/pdf?employee_id=${encodeURIComponent(r.id)}&token=${localStorage.getItem('hrms_token')}`, '_blank')} className={`bg-transparent border-0 text-[12px] font-semibold p-0 ${slipForEmp(r.id) ? "text-t2 cursor-pointer hover:underline" : "text-t3 cursor-not-allowed"}`}>Slip</button>
          </div> },
        ]} data={pagedTopE} />
        <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-[14px] bg-bg2/60 border-t border-b0">
          <div className="text-[12px] text-t3">Showing <strong className="text-t1">{topPageStart + 1}-{Math.min(topPageStart + topPageSize, filteredTopE.length)}</strong> of <strong className="text-t1">{filteredTopE.length}</strong> employees</div>
          <div className="flex items-center gap-[3px]">
            <button disabled={topSafePage === 1} onClick={() => setTopPage(p => Math.max(1, p - 1))} className={`w-7 h-7 rounded-lg flex items-center justify-center border-0 bg-transparent ${topSafePage === 1 ? "text-t3 opacity-40 cursor-not-allowed" : "text-t2 cursor-pointer hover:bg-bg2"}`}><ChevronLeft size={14} /></button>
            {topPageNumbers.map((n, i) => n === '…'
              ? <span key={"e" + i} className="px-1 text-t3 text-[13px]">…</span>
              : <button key={n} onClick={() => setTopPage(n)} className={`w-7 h-7 rounded-lg text-[13px] font-bold border-0 cursor-pointer ${n === topSafePage ? "text-white" : "bg-transparent text-t2 hover:bg-bg2"}`} style={n === topSafePage ? { background: K.p } : undefined}>{n}</button>
            )}
            <button disabled={topSafePage === topTotalPages} onClick={() => setTopPage(p => Math.min(topTotalPages, p + 1))} className={`w-7 h-7 rounded-lg flex items-center justify-center border-0 bg-transparent ${topSafePage === topTotalPages ? "text-t3 opacity-40 cursor-not-allowed" : "text-t2 cursor-pointer hover:bg-bg2"}`}><ChevronRight size={14} /></button>
          </div>
        </div>
      </> : <div className="text-center p-[30px] text-t3 text-[14px]">No employees match these filters</div>}
    </Cd>}

    {payTab === "process" && <div>
      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={Wallet} title="Run Payroll" />
        <div className="text-[14px] text-t3 mb-2">
          Running for: <span className="font-semibold text-t1">{companies.find(c => String(c.id) === selCompany)?.name || '—'}</span>
        </div>
        {/* Payroll is for the previous (completed) month only — the
            current month isn't over, so it can't be paid yet. */}
        <div className="flex gap-[10px] items-end">
          <div className="flex-1">
            <div className="text-[14px] text-t2 mb-[3px]">Payroll Month</div>
            <div className="px-[10px] py-[8px] bg-bg2 border border-b0 rounded-[10px] text-[14px] font-semibold text-t0">
              {fmtP(prevMonthPeriod)} <span className="text-[12px] text-t3 font-normal">(previous month)</span>
            </div>
          </div>
          <Bt disabled={!selCompany || previewBusy} onClick={async () => {
              if (!selCompany) return notify('Pick a company in the bar above');
              const cName = companies.find(c => String(c.id) === selCompany)?.name || '';
              const existing = uniqueRunsForCompany.find(r => r.period === prevMonthPeriod);
              const msg = existing
                ? `Re-process payroll for ${fmtP(prevMonthPeriod)} (${cName})? This recomputes base salary and resets generated payslips.`
                : `Process payroll for ${fmtP(prevMonthPeriod)} (${cName})?`;
              if (await confirmDialog(msg)) runPayroll(prevMonthPeriod);
            }}>{previewBusy ? 'Loading…' : 'Process Payroll'}</Bt>
        </div>
        <div className="text-[12px] text-t3 mt-2">
          Step 1 of the cycle. Generates base-salary slips and the Base Salary bank file. Add incentives and generate payslips next.
        </div>
      </Cd>
      <Cd><Sec icon={History} title="Payroll Run History (this company)" />
        <Tbl cols={[
          { k: "period", l: "Period", r: r => <span className="font-semibold text-[14px]">{fmtP(r.period)}</span> },
          { k: "emp", l: "Employees", r: r => <span className="font-semibold">{r.empCount}</span> },
          { k: "gross", l: "Gross", r: r => <span className="text-[14px]">{fmt$(r.gross)}</span> },
          { k: "ded", l: "Deductions", r: r => <span className="text-[14px] text-r">{fmt$(r.deductions)}</span> },
          { k: "net", l: "Net Pay", r: r => <span className="font-bold text-g">{fmt$(r.net)}</span> },
          { k: "er", l: "Employer Cost", r: r => <span className="text-[14px] text-pu">{fmt$(r.erCost)}</span> },
          { k: "st", l: "Status", r: r => <Bg c={stClrPay(r.status)} sm dot>{r.status}</Bg> },
          { k: "act", l: "", r: r => <div className="flex gap-[3px]">
            <Bt v="ghost" s="xs" icon={Eye} onClick={() => { setPayPeriod(r.period); loadSlips(r.period); setPayTab("payslips"); }}>Slips</Bt>
            {can.lock && r.status === "Completed" && r.payslipsGenerated && <Bt v="success" s="xs" icon={Check} disabled={lockingId === r.id} onClick={async () => { if ((await confirmDialog("Lock payroll for " + fmtP(r.period) + "? This finalises slips — no further changes."))) lockPayroll(r.id); }}>{lockingId === r.id ? "Locking..." : "Lock"}</Bt>}
          </div> },
        ]} data={uniqueRunsForCompany} />
      </Cd>
    </div>}

    {payTab === "incentives" && <div>
      {/* Org-wide pending-approval banner (admin/accounts) — lists every
          company+period awaiting approval with a one-click jump, so the
          admin never has to hunt company by company. */}
      {can.approveIncentive && pendingInc.total > 0 && <Cd style={{ marginBottom: 12, background: '#fffbeb', border: '1px solid #fcd34d' }}>
        <div className="text-[14px] font-bold text-a mb-2 flex items-center gap-1"><AlertTriangle size={13} /> {pendingInc.total} incentive(s) awaiting your approval</div>
        <div className="space-y-[6px]">
          {pendingInc.groups.map((g, i) => {
            const here = String(g.company_id) === selCompany && g.period === payPeriod;
            return <div key={i} className="flex items-center justify-between gap-2 text-[14px] bg-bg1 rounded-lg px-[10px] py-[6px] border border-b0">
              <span><span className="font-semibold text-t0">{g.company || ('Company ' + g.company_id)}</span> · {fmtP(g.period)} · <span className="text-a font-semibold">{g.count} pending</span> · ₹{g.transfer.toLocaleString('en-IN')}</span>
              {here ? <Bg c={K.g} sm>viewing</Bg> : <Bt v="secondary" s="xs" onClick={() => jumpToPending(g)}>Review</Bt>}
            </div>;
          })}
        </div>
      </Cd>}
      {/* Incentive upload — two-amount sheet (amount_to_transfer + tds_amount).
          Incentives are a SEPARATE payment: they drive Bank File #2 and
          combine onto the payslip (gross = transfer + tds). They do NOT
          change the base salary or Base Salary bank file. Previous month only. */}
      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={Gift} title="Incentives" />
        {!payPeriod && <div className="text-[14px] text-t3 mt-2">Pick the period ({fmtP(prevMonthPeriod)}) in the bar above.</div>}
        {payPeriod && (() => {
          const run = uniqueRunsForCompany.find(r => r.period === payPeriod);
          const runLocked = run?.status === 'Locked';
          const isPrevMonth = payPeriod === prevMonthPeriod;
          return <div>
          <div className="text-[14px] text-t2 mb-2 flex items-center gap-2 flex-wrap">
            <span>Period <span className="font-semibold text-t1">{fmtP(payPeriod)}</span> · Company <span className="font-semibold text-t1">{companies.find(c => String(c.id) === selCompany)?.name || '—'}</span></span>
            {runLocked && <Bg c={K.r} sm>Locked</Bg>}
          </div>

          {!isPrevMonth && <Cd style={{ marginBottom: 12, background: '#fffbeb', border: '1px solid #fcd34d' }}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} style={{ color: K.a, flexShrink: 0, marginTop: 2 }} />
              <div className="text-[14px] text-t1">Incentives can only be uploaded for the previous month (<span className="font-semibold">{fmtP(prevMonthPeriod)}</span>). Switch the period in the bar above.</div>
            </div>
          </Cd>}
          {runLocked && <Cd style={{ marginBottom: 12, background: '#fff5f5', border: '1px solid #fca5a5' }}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} style={{ color: K.r, flexShrink: 0, marginTop: 2 }} />
              <div className="text-[14px] text-t1"><span className="font-bold text-r">Payroll is locked for this period.</span> Incentives can no longer be changed.</div>
            </div>
          </Cd>}

          {/* Upload zone + template. New format: amount_to_transfer + tds_amount. */}
          <div className="flex flex-wrap gap-2 items-center mb-2">
            <label className={isPrevMonth && !runLocked ? "cursor-pointer" : "cursor-not-allowed opacity-50"}>
              <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} disabled={!isPrevMonth || runLocked}
                     onChange={e => { const f = e.target.files?.[0]; if (f) { uploadIncentives(f); e.target.value = ''; } }} />
              <span className="inline-flex items-center gap-1 px-3 py-[6px] bg-p text-white rounded-[8px] text-[14px] font-semibold">
                <Upload size={12} /> {incBusy ? 'Uploading…' : 'Upload File (.csv / .xlsx)'}
              </span>
            </label>
            <Bt v="secondary" s="sm" icon={FileText} onClick={() => serverCsv('/api/payroll/incentives/template', 'incentive-template.csv')}>Download Template</Bt>
            {incentives.count > 0 && <Bt v="secondary" s="sm" icon={Download} onClick={() => { setPayTab('bank'); loadBankFile(payPeriod, 'incentive'); }}>View Incentive Bank File</Bt>}
          </div>

          <div className="text-[12px] text-t3 mb-3">
            Columns: <code>employee_id, amount_to_transfer, tds_amount, remarks</code>. <span className="font-semibold">amount_to_transfer</span> is what the employee is paid (drives the Incentive bank file); <span className="font-semibold">tds_amount</span> is the tax withheld. Gross on the payslip = transfer + TDS. Multiple uploads stack.
          </div>

          {/* Last-upload feedback: staged + skipped (with reasons). */}
          {incUploadResult && <Cd style={{ marginBottom: 12, background: incUploadResult.skipped_count ? '#fff5f5' : '#f6fff8', border: `1px solid ${incUploadResult.skipped_count ? '#fca5a5' : '#c8e6d0'}` }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[14px] font-bold text-t0">Last Upload</div>
              <button onClick={() => setIncUploadResult(null)} className="text-t3 text-[14px] cursor-pointer bg-transparent border-0">Dismiss</button>
            </div>
            <div className="text-[14px] text-t1 mb-2">
              <span className="text-g font-semibold">{incUploadResult.uploaded_count}</span> staged
              {incUploadResult.skipped_count > 0 && <> · <span className="text-r font-semibold">{incUploadResult.skipped_count}</span> skipped</>}
            </div>
            {incUploadResult.skipped?.length > 0 && <div>
              <div className="text-[14px] font-bold text-r mb-1">Skipped rows:</div>
              <div className="text-[14px] text-t2 space-y-[2px] max-h-[150px] overflow-auto">
                {incUploadResult.skipped.map((s, i) => <div key={i}>· <span className="font-mono">{s.employee_id}</span> · transfer ₹{s.amount_to_transfer} — <span className="text-r">{s.reason}</span></div>)}
              </div>
            </div>}
          </Cd>}

          {/* Centre Heads can upload but not view the list — their
              uploads go to admin for approval. */}
          {!can.viewIncentive ? (
            <Cd style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <div className="text-[14px] text-t1">
                <span className="font-semibold text-bl">Submitted for approval.</span> Incentive sheets you upload are sent to an administrator for review. They'll be paid only after approval. You don't have access to the full incentive list.
              </div>
            </Cd>
          ) : incentives.rows.length === 0 ? (
            <div className="text-center text-[14px] text-t3 p-4">No incentives uploaded for this period yet.</div>
          ) : (
            <div>
              {/* Pending-approval banner (admin can approve all at once). */}
              {incentives.pending_count > 0 && <Cd style={{ marginBottom: 10, background: '#fffbeb', border: '1px solid #fcd34d' }}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[14px] text-t1">
                    <AlertTriangle size={12} style={{ color: K.a, display: 'inline', marginRight: 4 }} />
                    <span className="font-semibold text-a">{incentives.pending_count} incentive(s) awaiting approval</span> · ₹{incentives.pending_transfer.toLocaleString('en-IN')} transfer pending. Pending incentives are NOT in the bank file or payslip until approved.
                  </div>
                  {can.approveIncentive && !runLocked && <Bt v="success" s="xs" icon={Check} disabled={approveAllBusy} onClick={approveAllIncentives}>{approveAllBusy ? "Approving..." : "Approve All"}</Bt>}
                </div>
              </Cd>}
              <div className="text-[14px] text-t2 mb-2">
                {incentives.count} row{incentives.count === 1 ? '' : 's'} · Approved transfer <span className="font-semibold text-g">₹{incentives.total_transfer.toLocaleString('en-IN')}</span> · Approved TDS <span className="font-semibold text-r">₹{incentives.total_tds.toLocaleString('en-IN')}</span>
              </div>
              <Tbl cols={[
                { k: 'eid', l: 'Employee', r: r => <div><div className="text-[15px] font-bold text-t0">{r.name || '—'}</div><div className="text-[12px] text-t3">{r.eid} · {r.dept || '—'}</div></div> },
                { k: 'transfer', l: 'Amount to Transfer', r: r => <span className="font-bold text-g">₹{r.amount_to_transfer.toLocaleString('en-IN')}</span> },
                { k: 'tds', l: 'TDS', r: r => <span className="text-[14px] text-r">₹{r.tds_amount.toLocaleString('en-IN')}</span> },
                { k: 'gross', l: 'Gross', r: r => <span className="text-[14px] text-t1">₹{r.gross.toLocaleString('en-IN')}</span> },
                { k: 'status', l: 'Status', r: r => {
                  const c = r.approval_status === 'approved' ? K.g : r.approval_status === 'rejected' ? K.r : K.a;
                  return <Bg c={c} sm dot>{r.approval_status}</Bg>;
                } },
                { k: 'remarks', l: 'Remarks', r: r => <span className="text-[12px] text-t3">{r.remarks || '—'}</span> },
                { k: 'audit', l: 'Uploaded', r: r => {
                  const d = r.uploaded_at ? new Date(r.uploaded_at) : null;
                  const stamp = d ? `${fmtDMY(d)} ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : '—';
                  return <div title={d ? d.toISOString() : ''}><div className="text-[12px] text-t1">{stamp}</div><div className="text-[12px] text-t3">by {r.uploaded_by || '—'}</div></div>;
                } },
                { k: 'act', l: '', r: r => runLocked ? null : <div className="flex gap-1">
                  {can.approveIncentive && r.approval_status === 'pending' && <>
                    <Bt v="success" s="xs" icon={Check} disabled={incRowBusy === `${r.id}:approve`} onClick={() => approveIncentive(r.id)}>{incRowBusy === `${r.id}:approve` ? "Approving..." : "Approve"}</Bt>
                    <Bt v="danger" s="xs" icon={X} disabled={incRowBusy === `${r.id}:reject`} onClick={() => rejectIncentive(r.id)}>{incRowBusy === `${r.id}:reject` ? "Rejecting..." : "Reject"}</Bt>
                  </>}
                  {can.deleteIncentive && <Bt v="ghost" s="xs" icon={Trash2} disabled={incRowBusy === `${r.id}:delete`} onClick={() => deleteIncentive(r.id)}>{incRowBusy === `${r.id}:delete` ? "Removing..." : "Remove"}</Bt>}
                </div> },
              ]} data={incentives.rows} />
            </div>
          )}
        </div>;
        })()}
      </Cd>
    </div>}

    {payTab === "payslips" && <div>
      {!payPeriod && <div className="text-center p-[20px] text-t3 text-[14px]">Pick a period in the bar above to view payslips.</div>}
      {payPeriod && (() => {
        const run = uniqueRunsForCompany.find(r => r.period === payPeriod);
        const generated = run?.payslipsGenerated;
        const runLocked = run?.status === 'Locked';
        const isPrevMonth = payPeriod === prevMonthPeriod;
        return <Cd style={{ marginBottom: 12 }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[14px] font-bold text-t0">Salary Slips — {fmtP(payPeriod)}</div>
              <div className="text-[14px] text-t3 mt-[2px]">
                {!run ? 'Process payroll for this period first.'
                  : generated ? <span className="text-g font-semibold">✓ Salary slips generated{run.payslipsGeneratedAt ? ` on ${fmtDMY(run.payslipsGeneratedAt)}` : ''}</span>
                  : 'Combined slips (base salary + incentives). Generate to publish.'}
              </div>
            </div>
            <div className="flex gap-2">
              {can.generate && run && !runLocked && isPrevMonth && <Bt v={generated ? 'secondary' : 'primary'} s="sm" icon={FileText} disabled={generating} onClick={generatePayslips}>
                {generating ? 'Generating…' : generated ? 'Re-generate Payslips' : 'Generate Payslips'}
              </Bt>}
              {can.lock && run && generated && !runLocked && <Bt v="success" s="sm" icon={Check} disabled={lockingId === run.id} onClick={async () => { if (await confirmDialog(`Lock payroll for ${fmtP(payPeriod)}? Finalises slips — no further changes.`)) lockPayroll(run.id); }}>{lockingId === run.id ? "Locking..." : "Lock"}</Bt>}
              {runLocked && <Bg c={K.g} sm dot>Locked</Bg>}
            </div>
          </div>
        </Cd>;
      })()}
      {payPeriod && <div className="text-[14px] text-t2 mb-2">{paySlips.length} payslip{paySlips.length === 1 ? '' : 's'} for {fmtP(payPeriod)} <span className="text-t3">(figures shown are base salary + incentives combined)</span></div>}
      {paySlips.length > 0 ? <Tbl cols={[
        { k: "nm", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.nm} sz={22} /><div><div className="text-[15px] font-bold text-t0">{r.nm}</div><div className="text-[12px] text-t3">{r.eid} · {r.dept}</div></div></div> },
        { k: "basic", l: "Basic", r: r => <span className="text-[12px]">{fmt$(r.basic)}</span> },
        { k: "inc", l: "Incentive", r: r => r.incentive ? <span className="text-[12px] text-tl font-semibold">{fmt$(r.incentive)}</span> : <span className="text-[12px] text-t3">—</span> },
        { k: "lop", l: "Leave / LOP Cut", r: r => (r.lwpDeduction + r.absentDeduction + (r.flexDeduction || 0)) > 0 ? <span className="text-[12px] text-r" title={`Unpaid leave ${r.lopDaysLwp}d · Absent ${r.lopDaysAbsent}d${r.flexDeduction > 0 ? ` · Flexible hours short ${r.flexShortHours}h` : ''}`}>− {fmt$(r.lwpDeduction + r.absentDeduction + (r.flexDeduction || 0))}</span> : <span className="text-[12px] text-t3">—</span> },
        { k: "gross", l: "Gross", r: r => <span className="text-[14px] font-semibold">{fmt$(r.gross)}</span> },
        { k: "pf", l: "PF(EE)", r: r => <span className="text-[12px] text-r">{fmt$(r.pfEe)}</span> },
        { k: "tds", l: "TDS", r: r => <span className="text-[12px]">{fmt$(r.tds)}</span> },
        { k: "ded", l: "Total Ded", r: r => <span className="text-[12px] text-r font-semibold">{fmt$(r.totalDed)}</span> },
        { k: "net", l: "Net Pay", r: r => <span className="font-bold text-g">{fmt$(r.net)}</span> },
        { k: "st", l: "", r: r => <Bg c={r.status === "Paid" ? K.g : K.a} sm>{r.status}</Bg> },
        { k: "act", l: "", r: r => <Bt v="ghost" s="xs" icon={Eye} onClick={() => openSlipDetail(r.period, r.eid)}>YTD</Bt> },
      ]} data={paySlips} /> : <div className="text-center p-[30px] text-t3 text-[14px]">Select a period to view payslips</div>}
    </div>}

    {/* Slip detail drawer — base + incentive + FY-to-date side by side. */}
    {slipDetail && <div onMouseDown={() => setSlipDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onMouseDown={e => e.stopPropagation()} className="w-full max-w-[620px]" style={{ background: K.b0, borderRadius: 8, padding: 18, maxHeight: '85vh', overflow: 'auto', border: `1px solid ${K.b1}` }}>
        {slipDetail.loading && <div className="text-center text-[14px] text-t3 p-4">Loading…</div>}
        {slipDetail.error && <div className="text-center text-[14px] text-r p-4">Error: {slipDetail.error}</div>}
        {slipDetail.slip && (() => {
          const s = slipDetail.slip; const y = slipDetail.ytd;
          return <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[15px] font-bold text-t0">{s.nm}</div>
                <div className="text-[14px] text-t3">{s.eid} · {s.role || '—'} · {s.dept || '—'} · {s.company || '—'}</div>
              </div>
              <div className="text-right">
                <div className="text-[14px] text-t3">Period</div>
                <div className="text-[14px] font-semibold text-t0">{fmtP(s.period)}</div>
              </div>
            </div>
            <div className="text-[14px] text-t3 mb-3">Working: {s.workDays} · Present: {s.presentDays}{s.lopDays > 0 && <span className="text-r"> · LOP: {s.lopDays}{(s.lwpDeduction > 0 || s.absentDeduction > 0 || s.flexDeduction > 0) && ` (${s.lwpDeduction > 0 ? `LWP ${s.lopDaysLwp}d − ₹${s.lwpDeduction.toLocaleString('en-IN')}` : ''}${s.lwpDeduction > 0 && s.absentDeduction > 0 ? ', ' : ''}${s.absentDeduction > 0 ? `Absent ${s.lopDaysAbsent}d − ₹${s.absentDeduction.toLocaleString('en-IN')}` : ''}${s.flexDeduction > 0 ? `${s.lwpDeduction > 0 || s.absentDeduction > 0 ? ', ' : ''}Flexible hours short ${s.flexShortHours}h − ₹${s.flexDeduction.toLocaleString('en-IN')}` : ''})`}</span>}{s.prorated && <span className="text-a"> · Pro-rated</span>}</div>

            <div className="text-[14px] font-bold text-t0 mb-1 mt-2">{y.fy_label} · Year-to-Date ({y.months} month{y.months === 1 ? '' : 's'})</div>
            <div className="overflow-x-auto" style={{ border: `1px solid ${K.b1}`, borderRadius: 6 }}>
              <table style={{ width: '100%', minWidth: 420, fontSize: 10, borderCollapse: 'collapse' }}>
                <thead style={{ background: K.b1 }}>
                  <tr><th style={{ textAlign: 'left', padding: '6px 10px' }}>Component</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>This Month</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>FY YTD</th></tr>
                </thead>
                <tbody>
                  {[
                    ['Basic',             s.basic,    y.basic],
                    ['HRA',               s.hra,      y.hra],
                    ['Special',           s.spl,      y.spl],
                    ['Conveyance',        s.conv,     y.conv],
                    ['Medical',           s.med,      y.medical],
                    ['Overtime',          s.ot,       y.overtime],
                    ['Bonus',             s.bonus,    y.bonus],
                    ['Arrears',           s.arrears,  y.arrears],
                    ['Other Earnings',    s.otherEarn, null],
                  ].filter(([_, m, yt]) => m || yt).map(([l, m, yt]) => (
                    <tr key={l} style={{ borderTop: `1px solid ${K.b1}` }}>
                      <td style={{ padding: '4px 10px' }}>{l}</td>
                      <td style={{ textAlign: 'right', padding: '4px 10px' }}>{fmt$(m)}</td>
                      <td style={{ textAlign: 'right', padding: '4px 10px' }}>{fmt$(yt)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: `2px solid ${K.b1}`, background: K.b1 }}>
                    <td style={{ padding: '6px 10px', fontWeight: 700 }}>Gross</td>
                    <td style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 700 }}>{fmt$(s.gross)}</td>
                    <td style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 700 }}>{fmt$(y.gross)}</td>
                  </tr>
                  {[
                    ['Reimbursement (not in gross)', s.reimbursements, null],
                    ['BYOD (not in gross)',          s.byod, null],
                  ].filter(([_, m]) => m).map(([l, m]) => (
                    <tr key={l} style={{ borderTop: `1px solid ${K.b1}` }}>
                      <td style={{ padding: '4px 10px' }}>{l}</td>
                      <td style={{ textAlign: 'right', padding: '4px 10px' }}>{fmt$(m)}</td>
                      <td style={{ textAlign: 'right', padding: '4px 10px' }}>—</td>
                    </tr>
                  ))}
                  {[
                    ['PF (Employee)',     s.pfEe, y.pf_employee],
                    ['ESI (Employee)',    s.esiEe, y.esi_employee],
                    ['Professional Tax',  s.pt,    y.professional_tax],
                    ['TDS (Income Tax)',  s.tds,   y.tds],
                    ['Loan EMI',          s.loanEmi, null],
                    ['GHI',               s.ghiDeduction, null],
                    ['Work Deduction',    s.workDeduction, null],
                  ].filter(([_, m, yt]) => m || yt).map(([l, m, yt]) => (
                    <tr key={l} style={{ borderTop: `1px solid ${K.b1}`, color: K.r }}>
                      <td style={{ padding: '4px 10px' }}>{l}</td>
                      <td style={{ textAlign: 'right', padding: '4px 10px' }}>{fmt$(m)}</td>
                      <td style={{ textAlign: 'right', padding: '4px 10px' }}>{fmt$(yt)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: `2px solid ${K.b1}`, background: K.b1 }}>
                    <td style={{ padding: '6px 10px', fontWeight: 700 }}>Total Deductions</td>
                    <td style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 700, color: K.r }}>{fmt$(s.totalDed)}</td>
                    <td style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 700, color: K.r }}>{fmt$(y.total_deductions)}</td>
                  </tr>
                  <tr style={{ borderTop: `2px solid ${K.b1}`, background: K.g, color: 'white' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700 }}>Net Pay</td>
                    <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700 }}>{fmt$(s.net)}</td>
                    <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700 }}>{fmt$(y.net_pay)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-[12px] text-t3 mt-2 italic">YTD aggregates payslips from {y.fy_start} through {s.period}. TDS shown is income tax deducted so far this FY — Form 16 reconciles to this.</div>

            <div className="flex justify-end gap-2 mt-3">
              <Bt v="secondary" s="sm" icon={Download} onClick={() => window.open(`${BASE}/api/payroll/slips/${s.period}/pdf?employee_id=${encodeURIComponent(s.eid)}&token=${localStorage.getItem('hrms_token')}`, '_blank')}>Download PDF</Bt>
              <Bt v="ghost" s="sm" onClick={() => setSlipDetail(null)}>Close</Bt>
            </div>
          </div>;
        })()}
      </div>
    </div>}

    {payTab === "reports" && <div>
      {/* Period-pinned downloads — anomaly sheet (anyone over-paid?),
          full salary register, and statutory PF/ESI/PT registers.
          All endpoints honour centre scope on the server. */}
      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={Download} title="Period Reports & Compliance" />
        <div className="text-[14px] text-t3 mb-2">Period selected: <span className="font-semibold text-t1">{payPeriod ? fmtP(payPeriod) : '— pick from the bar above —'}</span></div>
        {payPeriod && <div className="flex gap-2 flex-wrap">
          <Bt v="secondary" s="sm" icon={AlertTriangle} onClick={() => serverCsv(`/api/payroll/over-paid/${payPeriod}?format=csv${cQuery}`, `payroll-overpaid-${payPeriod}.csv`)}>Anomaly Sheet (over-paid)</Bt>
          <Bt v="secondary" s="sm" icon={Download} onClick={() => serverCsv(`/api/payroll/register/${payPeriod}?format=csv${cQuery}`, `salary-register-${payPeriod}.csv`)}>Salary Register</Bt>
          <Bt v="secondary" s="sm" icon={Download} onClick={() => serverCsv(`/api/payroll/statutory/${payPeriod}?type=pf&format=csv${cQuery}`,  `pf-register-${payPeriod}.csv`)}>PF Register</Bt>
          <Bt v="secondary" s="sm" icon={Download} onClick={() => serverCsv(`/api/payroll/statutory/${payPeriod}?type=esi&format=csv${cQuery}`, `esi-register-${payPeriod}.csv`)}>ESI Register</Bt>
          <Bt v="secondary" s="sm" icon={Download} onClick={() => serverCsv(`/api/payroll/statutory/${payPeriod}?type=pt&format=csv${cQuery}`,  `pt-register-${payPeriod}.csv`)}>PT Register</Bt>
        </div>}
        {!payPeriod && <div className="text-[14px] text-t3">Pick a period to download reports.</div>}
      </Cd>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <Cd><Sec icon={BarChart2} title="Monthly Payroll Trend" />
          {trend.length > 0 ? <div>{trend.map(t => <div key={t.period} className="flex items-center gap-[6px] py-[5px] border-b border-b0">
            <span className="text-[14px] font-semibold text-t0 w-[60px]">{fmtP(t.period)}</span>
            <div className="flex-1 h-2 rounded bg-b1 overflow-hidden"><div className="h-full rounded" style={{ background: `linear-gradient(90deg,${K.p},${K.g})`, width: trend.length > 0 ? Math.round(t.net / Math.max(...trend.map(x => x.net)) * 100) + "%" : "0%" }} /></div>
            <span className="text-[12px] font-semibold text-g w-[60px] text-right">{fmt$(t.net)}</span>
          </div>)}</div> : <div className="text-[14px] text-t3">No data</div>}
        </Cd>
        <Cd><Sec icon={DollarSign} title="Component-wise Cost (Latest Month)" />
          {components.map(c => <div key={c.label} className="flex justify-between items-center py-1 border-b border-b0">
            <div className="flex items-center gap-[6px]">
              <div className="w-[6px] h-[6px] rounded-full" style={{ background: c.type === "earning" ? K.g : c.type === "deduction" ? K.r : K.bl }} />
              <span className="text-[14px] text-t1">{c.label}</span>
            </div>
            <span className={`text-[14px] font-semibold ${c.type === "earning" ? "text-g" : c.type === "deduction" ? "text-r" : "text-bl"}`}>{fmt$(c.amount)}</span>
          </div>)}
        </Cd>
      </div>
      <Cd><Sec icon={Building} title="Department-wise Payroll (Latest Month)" />
        <Tbl cols={[
          { k: "dept", l: "Department", r: r => <span className="font-semibold text-[14px]">{r.dept || "—"}</span> },
          { k: "count", l: "Headcount", r: r => <span className="font-semibold">{r.count}</span> },
          { k: "gross", l: "Gross", r: r => <span className="text-[14px]">{fmt$(r.gross)}</span> },
          { k: "net", l: "Net Pay", r: r => <span className="font-semibold text-g">{fmt$(r.net)}</span> },
          { k: "er", l: "Employer Cost", r: r => <span className="text-[14px] text-pu">{fmt$(r.erCost)}</span> },
        ]} data={deptWise} />
      </Cd>
    </div>}

    {payTab === "bank" && <div>
      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={Building} title="Bank Transfer Files" />
        {/* Two separate bank files: Base Salary (from processed slips) and
            Incentives (from uploaded amount_to_transfer). Toggle between them. */}
        <div className="flex gap-2 mb-3">
          <Bt v={bankType === 'base' ? 'primary' : 'secondary'} s="sm" icon={Wallet}
              onClick={() => { if (payPeriod) loadBankFile(payPeriod, 'base'); }} disabled={!payPeriod}>Base Salary</Bt>
          <Bt v={bankType === 'incentive' ? 'primary' : 'secondary'} s="sm" icon={Gift}
              onClick={() => { if (payPeriod) loadBankFile(payPeriod, 'incentive'); }} disabled={!payPeriod}>Incentives</Bt>
        </div>
        <div className="flex gap-[10px] items-end flex-wrap">
          <div className="flex-1 min-w-[200px] text-[14px] text-t3">
            {bankType === 'incentive' ? 'Incentive' : 'Base Salary'} file · Period: <span className="font-semibold text-t1">{payPeriod ? fmtP(payPeriod) : '— pick from the bar above —'}</span>
          </div>
          {payBankFile && payPeriod && <>
            <Bt v="secondary" icon={Download} onClick={() => downloadBankFile('generic', payBankFile)}>Generic CSV</Bt>
            <Bt v="secondary" icon={Download} onClick={() => downloadBankFile('hdfc',    payBankFile)}>HDFC NEFT</Bt>
            <Bt v="secondary" icon={Download} onClick={() => downloadBankFile('icici',   payBankFile)}>ICICI</Bt>
            <Bt v="secondary" icon={Download} onClick={() => downloadBankFile('axis',    payBankFile)}>Axis</Bt>
          </>}
        </div>
      </Cd>
      {payBankFile && <div>
        <KpiStrip items={[
          { icon: Users,    label: "Employees", value: String(payBankFile.count),  c: K.bl },
          { icon: Wallet,   label: bankType === 'incentive' ? "Total Transfer" : "Total Net", value: fmt$(payBankFile.total), c: K.g },
          ...(bankType === 'incentive' ? [{ icon: AlertTriangle, label: "Total TDS", value: fmt$(payBankFile.total_tds || 0), c: K.r }] : []),
          { icon: Building, label: "Period",    value: fmtP(payBankFile.period),   c: K.p  },
        ]} />
        <Cd><Tbl cols={[
          { k: "nm", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.nm} sz={20} /><span className="text-[15px] font-bold text-t0">{r.nm}</span></div> },
          { k: "net", l: bankType === 'incentive' ? "Amount to Transfer" : "Net Pay", r: r => <span className="font-bold text-g">{fmt$(r.net)}</span> },
          ...(bankType === 'incentive' ? [{ k: "tds", l: "TDS", r: r => <span className="text-[12px] text-r">{fmt$(r.tds || 0)}</span> }] : []),
          { k: "bank", l: "Bank", r: r => <span className="text-[12px] text-t1">{r.bank || "—"}</span> },
          { k: "acct", l: "Account", r: r => <span className="text-[12px] text-t1 font-mono">{r.acct || "—"}</span> },
          { k: "ifsc", l: "IFSC", r: r => <span className="text-[12px] text-t2">{r.ifsc || "—"}</span> },
          { k: "mode", l: "Mode", r: r => <Bg c={K.bl} sm>{r.mode || "Bank"}</Bg> },
        ]} data={payBankFile.transfers} /></Cd>
      </div>}
      {!payBankFile && <div className="text-center p-[30px] text-t3 text-[14px]">Pick a period in the bar above to view the bank transfer file</div>}
    </div>}
    {processPreview && <ProcessPreviewModal preview={processPreview} onCancel={() => setProcessPreview(null)} onConfirm={confirmRunPayroll} fmt$={fmt$} canPick={can.processSelected} />}
  </div>;
};

// ── Process Payroll pre-flight modal ──
// Shown after clicking "Process Payroll" but BEFORE the run actually executes.
// Lists who would be processed and who would be silently skipped (no annual
// salary on file). User can cancel and go fix the missing data, or proceed.
const ProcessPreviewModal = ({ preview, onCancel, onConfirm, fmt$, canPick = false }) => {
  const { company, period, totals, eligible = [], skipped = [], locked, busy } = preview;
  const [showAllSkipped, setShowAllSkipped] = React.useState(false);
  const skippedToShow = showAllSkipped ? skipped : skipped.slice(0, 8);
  const hasSkipped = skipped.length > 0;
  const noOne = totals.eligible === 0;
  // "Everyone" (default) or "Choose employees" — process only the people ticked.
  const [pickMode, setPickMode] = React.useState(false);
  const [picked, setPicked] = React.useState(() => new Set());
  const [q, setQ] = React.useState('');
  const shown = eligible.filter(e => !q.trim() || (e.name + ' ' + e.id).toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShownPicked = shown.length > 0 && shown.every(e => picked.has(e.id));
  const toggleShown = () => setPicked(p => { const n = new Set(p); shown.forEach(e => allShownPicked ? n.delete(e.id) : n.add(e.id)); return n; });
  const goCount = pickMode ? picked.size : totals.eligible;
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={busy ? undefined : onCancel}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[824px] max-h-[88vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-2">
          <span className="text-[18px] font-extrabold text-t0">Process Payroll — Confirmation</span>
          {!busy && <button onClick={onCancel} className="bg-transparent border-0 text-t3 cursor-pointer text-[18px]">✕</button>}
        </div>
        <div className="text-[14px] text-t2 mb-4">{company?.name} · {period}</div>

        {locked && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-rb border border-rd text-[14px] text-r">
            This payroll run is already <b>locked</b>. Processing again will fail.
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-bg2 border border-b0 rounded-lg p-2 text-center">
            <div className="text-[12px] text-t3 uppercase tracking-wide">Active</div>
            <div className="text-[18px] font-extrabold text-t0">{totals.active}</div>
          </div>
          <div className="rounded-lg p-2 text-center" style={{ background: 'rgba(16,185,129,.10)', border: '1px solid rgba(16,185,129,.30)' }}>
            <div className="text-[12px] text-t3 uppercase tracking-wide">Will be Processed</div>
            <div className="text-[18px] font-extrabold text-g">{totals.eligible}</div>
          </div>
          <div className="rounded-lg p-2 text-center" style={{ background: hasSkipped ? 'rgba(239,68,68,.10)' : 'rgba(156,163,175,.10)', border: `1px solid ${hasSkipped ? 'rgba(239,68,68,.30)' : 'rgba(156,163,175,.30)'}` }}>
            <div className="text-[12px] text-t3 uppercase tracking-wide">Will be Skipped</div>
            <div className="text-[18px] font-extrabold" style={{ color: hasSkipped ? K.r : K.t3 }}>{totals.skipped}</div>
          </div>
        </div>

        {hasSkipped && (
          <div className="mb-4">
            <div className="text-[14px] font-bold text-r mb-1">⚠ {totals.skipped} employee{totals.skipped === 1 ? '' : 's'} will be skipped — no annual salary on file</div>
            <div className="text-[14px] text-t2 mb-2">Set the annual gross salary on each employee's profile, then re-run Process Payroll. Otherwise these employees will get no payslip this month.</div>
            <div className="bg-bg2 border border-b0 rounded-lg max-h-[200px] overflow-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead className="bg-bg3"><tr>
                  <th className="px-2 py-[5px] text-left text-t3 text-[12px] uppercase">Employee</th>
                  <th className="px-2 py-[5px] text-left text-t3 text-[12px] uppercase">ID</th>
                </tr></thead>
                <tbody>
                  {skippedToShow.map(e => (
                    <tr key={e.id} className="border-b border-b0 last:border-0">
                      <td className="px-2 py-[5px] text-t0 font-medium">{e.name}</td>
                      <td className="px-2 py-[5px] text-t2 font-mono text-[12px]">{e.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {skipped.length > skippedToShow.length && (
                <button onClick={() => setShowAllSkipped(true)} className="w-full text-center py-[6px] bg-transparent border-0 text-[14px] text-bl font-semibold cursor-pointer hover:underline">
                  Show all {skipped.length}
                </button>
              )}
            </div>
          </div>
        )}

        {noOne && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-ab border border-ad text-[14px] text-a">
            <b>No one is eligible.</b> Processing now will not create any payslip. Fix the skipped employees above and run again.
          </div>
        )}

        {!hasSkipped && !noOne && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-gb border border-gd text-[14px] text-g">
            All active employees have salaries on file. Safe to proceed.
          </div>
        )}

        {!noOne && canPick && (
          <div className="mb-4">
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setPickMode(false)} className={`px-3 py-[6px] rounded-lg text-[14px] font-semibold cursor-pointer border ${!pickMode ? 'bg-bl text-white border-bl' : 'bg-bg2 text-t1 border-b1'}`}>Everyone ({totals.eligible})</button>
              <button type="button" onClick={() => setPickMode(true)} className={`px-3 py-[6px] rounded-lg text-[14px] font-semibold cursor-pointer border ${pickMode ? 'bg-bl text-white border-bl' : 'bg-bg2 text-t1 border-b1'}`}>Choose employees…</button>
            </div>
            {pickMode && (
              <div className="bg-bg2 border border-b0 rounded-lg">
                <div className="flex items-center gap-2 p-2 border-b border-b0">
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or ID…" className="flex-1 bg-bg1 border border-b1 rounded px-2 py-[5px] text-[14px] text-t0 focus:outline-none" />
                  <button type="button" onClick={toggleShown} className="bg-transparent border-0 text-[14px] text-bl font-semibold cursor-pointer whitespace-nowrap">{allShownPicked ? 'Clear shown' : 'Select shown'}</button>
                </div>
                <div className="max-h-[260px] overflow-auto">
                  {shown.map(e => (
                    <label key={e.id} className="flex items-center gap-2 px-3 py-[6px] border-b border-b0 last:border-0 cursor-pointer hover:bg-sh">
                      <input type="checkbox" checked={picked.has(e.id)} onChange={() => toggle(e.id)} />
                      <span className="text-[14px] text-t0 font-medium flex-1">{e.name}</span>
                      <span className="text-[12px] text-t3 font-mono">{e.id}</span>
                    </label>
                  ))}
                  {shown.length === 0 && <div className="p-3 text-center text-[14px] text-t3">No matches.</div>}
                </div>
                <div className="px-3 py-[6px] text-[12px] text-t3 border-t border-b0">{picked.size} selected · everyone else's payslip for {period} stays exactly as it is.</div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-[6px]">
          <Bt v="secondary" full onClick={onCancel} disabled={busy}>Cancel — make changes</Bt>
          <Bt v="success" full onClick={() => onConfirm(canPick && pickMode ? [...picked] : null)} disabled={busy || noOne || locked || (pickMode && picked.size === 0)}>
            {busy ? 'Processing…' : pickMode ? `Process ${goCount} selected employee${goCount === 1 ? '' : 's'}` : `Continue & Process ${totals.eligible} Employee${totals.eligible === 1 ? '' : 's'}`}
          </Bt>
        </div>
      </div>
    </div>
  );
};
